import type RBush from 'rbush';

import { PinService } from '@/microfrontends/map/shared/services/PinService';
import {
  type HotspotFeature,
  type HotspotFeatureCollection,
  type HotspotProperties,
  type RBushItem,
} from '@/microfrontends/map/shared/types/Map';
import { getTilePixelCoords } from '@/microfrontends/map/shared/utils/map/tiles';

import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';

import { TILE_SIZE } from '../../../constants/map';
import { POINT_RADIUS } from '../../../services/PinService/impl/constants';

import { HOTSPOTS_LAYER_ZINDEX } from './constants';

export type HotspotsLayerOptions = {
  map: YMaps.Map;
  rbush: RBush<RBushItem>;
};

export function defineHotspotsLayer(ymaps: YMaps.IYMaps): void {
  if (ymaps.modules.isDefined('HotspotsLayer')) {
    return;
  }

  ymaps.modules.define<[typeof YMaps.hotspot.Layer, typeof YMaps.hotspot.ObjectSource]>(
    'HotspotsLayer',
    // Два модуля зависимостей в конце - обязательные, хоть и не используются в классе, не удалять
    ['hotspot.Layer', 'hotspot.ObjectSource', 'util.hd', 'util.pixelBounds'],
    (provide, hsLayer, hsObjectSource) => {
      class HotspotsLayer {
        public readonly layer: YMaps.hotspot.Layer;
        public readonly objectSource: YMaps.hotspot.ObjectSource;
        private map: YMaps.Map;
        private mapProjection: YMaps.projection.wgs84Mercator;
        private rbush: RBush<RBushItem>;

        public constructor(options: HotspotsLayerOptions) {
          this.map = options.map;
          this.mapProjection = this.map.options.get('projection');
          this.rbush = options.rbush;

          this.objectSource = new hsObjectSource(() => null);
          this.objectSource.requestObjects = this.requestObjects;

          this.layer = new hsLayer(this.objectSource, { zIndex: HOTSPOTS_LAYER_ZINDEX });
        }

        private requestObjects = (
          layer: YMaps.hotspot.Layer,
          tileNumber: YMaps.TTileNumber,
          zoom: number,
          callback: Function,
        ): void => {
          if (this.objectSource.restrict(layer, tileNumber, zoom)) {
            callback([]);

            return;
          }

          // Получили координаты тайла
          const tilePixelCoords = getTilePixelCoords(tileNumber);

          // Получили все кластеры, которые входят в тайл
          const tileClusters = this.getClustersForTile(tilePixelCoords, zoom);

          if (!tileClusters.length) {
            callback([]);

            return;
          }

          const response = this.convertClustersToHotspotsResponse(tileClusters, tilePixelCoords, zoom);

          this.objectSource.parseResponse(layer, response, callback, tileNumber, zoom);
        };

        private calculateHotspotCoordinates = (
          cluster: Cluster,
          tilePixelCoords: YMaps.TPixelCoord,
          tileZoom: number,
        ): [[number, number], [number, number]] => {
          const [coordX, coordY] = cluster.geometry.coordinates;
          const [pixelCoordX, pixelCoordY] = this.mapProjection.toGlobalPixels([coordX, coordY], tileZoom);

          const [tilePixelCoordX, tilePixelCoordY] = tilePixelCoords;

          // вычислим координаты x и y относительно тайла
          const [relativeX, relativeY] = [pixelCoordX - tilePixelCoordX, pixelCoordY - tilePixelCoordY];

          const pinService = new PinService({ cluster });
          const { radius } = pinService.provideSizes();

          const rectLeftTop: [number, number] = [relativeX - radius, relativeY - radius];
          const rectRightBottom: [number, number] = [relativeX + radius, relativeY + radius];

          return [rectLeftTop, rectRightBottom];
        };

        private convertClusterToHotspot = (
          cluster: Cluster,
          tilePixelCoords: YMaps.TPixelCoord,
          zoom: number,
        ): HotspotFeature => {
          const coordinates = this.calculateHotspotCoordinates(cluster, tilePixelCoords, zoom);
          const properties: HotspotProperties = {
            HotspotMetaData: {
              RenderedGeometry: {
                coordinates,
                type: 'Rectangle',
              },
              id: cluster.id,
            },
            clusterData: cluster,
          };

          return {
            properties,
            type: 'Feature',
          };
        };

        private convertClustersToHotspotsResponse = (
          clusters: Cluster[],
          tilePixelCoords: YMaps.TPixelCoord,
          zoom: number,
        ): { data: HotspotFeatureCollection } => {
          const features = clusters.map(cluster => this.convertClusterToHotspot(cluster, tilePixelCoords, zoom));

          return {
            data: {
              features,
              type: 'FeatureCollection',
            },
          };
        };

        private getClustersForTile(tilePixelCoords: YMaps.TPixelCoord, zoom: number): Cluster[] {
          const minX = tilePixelCoords[0] - POINT_RADIUS;
          const minY = tilePixelCoords[1] - POINT_RADIUS;
          const maxX = minX + TILE_SIZE + POINT_RADIUS * 2;
          const maxY = minY + TILE_SIZE + POINT_RADIUS * 2;

          const [coordMaxX, coordMinY] = this.mapProjection.fromGlobalPixels([minX, minY], zoom);
          const [coordMinX, coordMaxY] = this.mapProjection.fromGlobalPixels([maxX, maxY], zoom);

          const rBushItems = this.rbush.search({
            maxX: coordMaxX,
            maxY: coordMaxY,
            minX: coordMinX,
            minY: coordMinY,
          });

          return rBushItems.map(({ cluster }) => cluster);
        }
      }

      provide(HotspotsLayer);
    },
  );
}
